import UserService from "api/user/user";
import {PolIcon} from "components/PolIcon/policon";
import React, {useEffect} from 'react';
import PolTableTdCustomerFiles
    from "v4/components/ui/PolTable/components/PolTableTd/components/PolTableTdCustomerFiles/PolTableTdCustomerFiles";
import {IMAGE, LIVEO_CALLS} from "v4/data/apiRoutes";
import useFetch from "v4/hooks/useFetch";
import useNotification from "v4/hooks/useNotification";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import {generateUrl} from "v4/services/api.service";
import {getFormattedValue} from "v4/utils";
import PolTableTdRowActions from "./components/PolTableTdRowActions/PolTableTdRowActions";
import {TEL_TYPE} from "v4/data/fieldTypes";

export default function PolTableTd({value, type, rowActions, dataLabel, rowMetadata, cellMetadata}) {
    const {t} = usePolTranslation();
    const {addSuccessNotification, addInfoNotification, addErrorNotification} = useNotification();
    const isLiveo = UserService.isLiveo();
    const hasLiveoConfigured = UserService.hasLiveoConfigured();

    const [{isError}, fetchLiveo] = useFetch();

    const handleLiveoCall = (rowMetadata?.name && value) ? () => {
        if (hasLiveoConfigured) {
            fetchLiveo({
                url: generateUrl(LIVEO_CALLS),
                config: {
                    method: 'POST',
                    body: JSON.stringify({
                        contactName: rowMetadata.name,
                        number: value,
                    })
                }
            })
            addSuccessNotification(t('calling'));
        } else {
            addInfoNotification(t('liveo_not_configured'));
        }
    } : null;

    useEffect(() => {
        if (isError) {
            addErrorNotification(t('error_liveo'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError])

    switch (type) {
        case 'image':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {value
                        ? (
                            /* eslint-disable-next-line jsx-a11y/img-redundant-alt */
                            <img src={generateUrl(IMAGE, {id: value})}
                                 alt="Image non trouvée"
                                 width="100"
                                 height="100"/>
                        )
                        : (
                            <div>-</div>
                        )
                    }
                </td>
            )
        case TEL_TYPE:
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {
                        isLiveo && handleLiveoCall
                            ? (
                                <span className="liveo-format" onClick={handleLiveoCall}>
                                    <PolIcon icon="phone"/> {value}
                                </span>
                            ) : (
                                value
                                    ? <a href={`tel:${value}`} className="liveo-format">{value}</a>
                                    : '-'
                            )
                    }
                </td>
            )
        case 'money':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {value
                        ? Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'})?.format(value) /* TODO: n'afficher qu'en euro ou peut être évolutif ? */
                        : '-'
                    }
                </td>
            )
        case 'percent':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {
                        value
                            ? isNaN(value) ? value : `${value}%`
                            : '-'
                    }
                </td>
            )
        case 'email':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {
                        value
                            ? <a href={`mailto:${value}`}>{value}</a>
                            : '-'
                    }
                </td>
            )
        case 'url':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {value
                        ? (
                            <a href={`${value.trim()}`}
                               target="_blank"
                               rel="noopener noreferrer">
                                {value}
                            </a>
                        )
                        : '-'
                    }
                </td>
            )
        case 'date':
        case 'datetime':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {getFormattedValue(type, value, {keepTime: true})}
                </td>
            )
        case 'files':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    <PolTableTdCustomerFiles files={rowMetadata?.files ?? []}/>
                </td>
            )
        case 'longtext':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    <div>
                        {
                            value?.length > 0
                                ? value?.length > 100 ? value.substring(0, 100).trim() + '...' : value
                                : '-'
                        }
                    </div>
                </td>
            )
        case 'rowActions':
            return (
                <td data-type={type}>
                    <PolTableTdRowActions rowActions={rowActions} rowMetadata={rowMetadata}/>
                </td>
            )
        case 'icon': {
            const icon = cellMetadata?.icon ?? 'circle-notch';
            const iconColor = cellMetadata?.iconColor ?? 'grey';
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    <PolIcon icon={icon} color={iconColor}/>
                </td>
            )
        }
        case 'html':
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    <span dangerouslySetInnerHTML={{__html: value}}/>
                </td>
            )
        default:
            return (
                <td data-label={dataLabel}
                    data-type={type}>
                    {autoFormatValue(value)}
                    {cellMetadata?.isUnknown && <span className="pol-table__badge">{t('unknown')}</span>}
                </td>
            )
    }
}

function autoFormatValue(value) {
    if (Array.isArray(value)) {
        return value.join(', ');
    }

    return value ?? '-';
}
